/**
 * Deprecation shims for server actions whose ID is about to be invalidated.
 *
 * Background: with `NEXT_SERVER_ACTIONS_ENCRYPTION_KEY` pinned (see
 * `infra/secret.ts`), server-action IDs are stable across deploys for
 * unchanged actions — most deploys are invisible to open client tabs. But
 * renaming, removing, or breaking-signature-changing an action still
 * invalidates its ID for tabs opened before the deploy. The discipline (see
 * the "Server Action Lifecycle" section in CLAUDE.md) is to leave the original
 * export in place for one deploy cycle, calling `throwDeprecatedAction(...)`,
 * so old tabs get a typed, recognizable error instead of an opaque 500.
 *
 * The thrown error's message intentionally matches the existing
 * `SERVER_ACTION_SKEW_PATTERN` regex in
 * `packages/frontend/src/components/route-error-classification.ts`
 * ("was not found on the server"), and its `name` is matched explicitly by the
 * same detector and by `isStaleDeploymentError` in
 * `packages/frontend/app/get-query-client.ts`. Either match routes the error
 * through the version-skew detection path → the "Update available" toast (or,
 * with the soft-refresh flag off, the legacy reload).
 */

/**
 * Error thrown by a deprecated/renamed server-action shim to tell an old
 * client tab it is running against a newer server build and needs to refresh.
 *
 * Matched two ways by the client-side stale-deploy detector:
 *  1. By `name === "DeprecatedActionError"` (primary).
 *  2. By the message text "was not found on the server" matching
 *     `SERVER_ACTION_SKEW_PATTERN` (belt-and-suspenders, in case the error
 *     `name` is stripped crossing a worker/serialization boundary).
 */
export class DeprecatedActionError extends Error {
  /** The replacement action name to use, if one exists. */
  readonly replacement: string | undefined;

  constructor(replacement?: string) {
    const tail = replacement ? ` Use \`${replacement}\` instead.` : "";
    super(
      `Server action was not found on the server. It has been deprecated.${tail}`,
    );
    this.name = "DeprecatedActionError";
    this.replacement = replacement;
  }
}

/**
 * Throw from a deprecated/renamed server-action shim. The original export
 * path stays callable from old client bundles for one deploy cycle, surfacing
 * the "Update available" toast instead of an opaque error. Delete the shim in
 * a follow-up PR once a full deploy cycle has passed.
 *
 * @example
 *   "use server";
 *   import { throwDeprecatedAction } from "@brev-io/common/src/server-actions/deprecated";
 *   export async function oldActionName() {
 *     throwDeprecatedAction("newActionName");
 *   }
 *
 * @param replacement Optional name of the action that replaces this one.
 * @returns never — always throws {@link DeprecatedActionError}.
 */
export function throwDeprecatedAction(replacement?: string): never {
  throw new DeprecatedActionError(replacement);
}
